import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { useLang } from "@/context";
import { T } from "@/i18n";
import api from "@/api";

export function LeadForm({ property = null, type = "contact", compact = false, testIdPrefix = "lead-form" }) {
  const { lang } = useLang();
  const t = T[lang];
  const f = t.form;
  const [data, setData] = useState({
    name: "", email: "", phone: "", country: "", budget: "", timeline: "",
    message: property ? f.messagePrefill.replace("{title}", property.title[lang]).replace("{ref}", property.ref_code) : "",
    contact_pref: "", privacy: false, newsletter: false, company: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (type !== "newsletter" && !data.name.trim()) return setError(f.required);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email)) return setError(f.invalidEmail);
    if (!data.privacy) return setError(f.privacyRequired);
    setStatus("sending");
    try {
      await api.post("/leads", {
        ...data, type, lang, page: window.location.pathname,
        property_ref: property?.ref_code || "", property_title: property?.title?.[lang] || "",
      });
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : T[lang].common.errorGeneric);
    }
  };

  if (status === "done") {
    return (
      <div className="rounded-2xl bg-[#2FA3A0]/10 border border-[#2FA3A0]/30 p-6 text-center" data-testid={`${testIdPrefix}-success`}>
        <CheckCircle2 className="mx-auto text-[#2FA3A0]" size={36} />
        <p className="mt-3 font-medium text-[#0E4D5C]">{type === "guide" ? t.home.guideSuccess : type === "newsletter" ? f.newsletterSuccess : f.success}</p>
      </div>
    );
  }

  const isGuide = type === "guide";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid={testIdPrefix}>
      <input type="text" value={data.company} onChange={(e) => set("company", e.target.value)} tabIndex="-1" autoComplete="off"
        className="absolute opacity-0 pointer-events-none h-0 w-0" aria-hidden="true" />
      {!isGuide && type !== "newsletter" && (
        <div>
          <label className="label-site" htmlFor={`${testIdPrefix}-name`}>{f.name} *</label>
          <input id={`${testIdPrefix}-name`} data-testid={`${testIdPrefix}-name-input`} className="input-site" value={data.name} onChange={(e) => set("name", e.target.value)} required />
        </div>
      )}
      {isGuide && (
        <div>
          <label className="label-site" htmlFor={`${testIdPrefix}-name`}>{f.guideName} *</label>
          <input id={`${testIdPrefix}-name`} data-testid={`${testIdPrefix}-name-input`} className="input-site" value={data.name} onChange={(e) => set("name", e.target.value)} required />
        </div>
      )}
      <div>
        <label className="label-site" htmlFor={`${testIdPrefix}-email`}>{f.email} *</label>
        <input id={`${testIdPrefix}-email`} type="email" data-testid={`${testIdPrefix}-email-input`} className="input-site" value={data.email} onChange={(e) => set("email", e.target.value)} required />
      </div>
      {!compact && !isGuide && (
        <>
          <div>
            <label className="label-site" htmlFor={`${testIdPrefix}-phone`}>{f.phone} *</label>
            <input id={`${testIdPrefix}-phone`} data-testid={`${testIdPrefix}-phone-input`} className="input-site" placeholder={f.phoneHint} value={data.phone} onChange={(e) => set("phone", e.target.value)} required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-site">{f.country}</label>
              <select className="input-site" data-testid={`${testIdPrefix}-country-select`} value={data.country} onChange={(e) => set("country", e.target.value)}>
                <option value="">{f.selectPlaceholder}</option>
                {f.countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label-site">{f.budget}</label>
              <select className="input-site" data-testid={`${testIdPrefix}-budget-select`} value={data.budget} onChange={(e) => set("budget", e.target.value)}>
                <option value="">{f.selectPlaceholder}</option>
                {f.budgetOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-site">{f.timeline}</label>
              <select className="input-site" data-testid={`${testIdPrefix}-timeline-select`} value={data.timeline} onChange={(e) => set("timeline", e.target.value)}>
                <option value="">{f.selectPlaceholder}</option>
                {f.timelineOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label-site">{f.contactPref}</label>
              <select className="input-site" data-testid={`${testIdPrefix}-pref-select`} value={data.contact_pref} onChange={(e) => set("contact_pref", e.target.value)}>
                <option value="">{f.selectPlaceholder}</option>
                {f.contactPrefOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label-site" htmlFor={`${testIdPrefix}-message`}>{f.message}</label>
            <textarea id={`${testIdPrefix}-message`} rows={4} data-testid={`${testIdPrefix}-message-input`} className="input-site" value={data.message} onChange={(e) => set("message", e.target.value)} />
          </div>
        </>
      )}
      <label className="flex items-start gap-3 text-sm text-[#1F2328]/80 cursor-pointer">
        <input type="checkbox" checked={data.privacy} onChange={(e) => set("privacy", e.target.checked)} data-testid={`${testIdPrefix}-privacy-checkbox`} className="mt-1 h-4 w-4 accent-[#B5472B]" />
        <span>{f.privacy} *</span>
      </label>
      <label className="flex items-start gap-3 text-sm text-[#1F2328]/60 cursor-pointer">
        <input type="checkbox" checked={data.newsletter} onChange={(e) => set("newsletter", e.target.checked)} data-testid={`${testIdPrefix}-newsletter-checkbox`} className="mt-1 h-4 w-4 accent-[#B5472B]" />
        <span>{f.newsletter}</span>
      </label>
      {error && <p className="text-sm text-[#B5472B] font-medium" data-testid={`${testIdPrefix}-error`}>{error}</p>}
      <button type="submit" disabled={status === "sending"} className="btn-primary w-full" data-testid={`${testIdPrefix}-submit-button`}>
        {status === "sending" ? t.common.sending : isGuide ? f.guideSubmit : f.submit}
      </button>
    </form>
  );
}
